import { useEventListQuery } from "@ticketingplatform/api/dist/event";
import { ColumnsType } from "antd/es/table";
import { useCallback, useMemo } from "react";
import { generatePath, Link } from "react-router-dom";
import { Space } from "antd";
import { CalendarOutlined, EditOutlined, EyeOutlined } from "@ant-design/icons";
import { useIntl } from "react-intl";
import { Event } from "@ticketingplatform/api/dist/event/fragments/__generated__/Event";
import { useTableQueryParams } from "hooks/useTableQueryParams";
import { pageToSkip, renderTableDateZonedColumn } from "utils/tableUtils";
import {
  getColumnTextFilterProps,
  getTextFilterInput,
} from "components/data/TableTextFilter/utils";
import { useVenueContext } from "contexts/VenueContext";
import { ROUTE_PATHS } from "const/paths";
import {
  EventStatus,
  EventType,
} from "@ticketingplatform/api/dist/__generated__/globalTypes";
import { GetEventsVariables } from "@ticketingplatform/api/dist/event/__generated__/GetEvents";
import {
  getColumnEnumFilterProps,
  getEnumFilterValue,
} from "components/data/TableEnumFilter/utils";

import { UseEventListReturnType } from "./types";

interface EventListHookParams {
  excludeRecurringEvents?: boolean;
  templateId?: number;
}

interface EventListHook {
  options?: EventListHookParams;
}

export const useEventList = ({
  options = {},
}: EventListHook = {}): UseEventListReturnType => {
  const { venue } = useVenueContext();
  const [{ page, pageSize, orderBy, filters }, setTableQueryParams] =
    useTableQueryParams<Event>();

  const variables = useMemo<GetEventsVariables>(
    () => ({
      venueInclude: false,
      imageInclude: false,
      seatMapInclude: false,
      ticketPoolsInclude: false,
      presalePasswordsInclude: false,
      signUpOptionsInclude: false,
      discountCodesInclude: false,
      take: pageSize,
      skip: pageToSkip(page, pageSize),
      filters: {
        ...(venue ? { venueId: { equals: venue.id } } : {}),
        ...getTextFilterInput(filters?.name, "name"),
        ...getEnumFilterValue(filters?.type, "type", EventType),
        ...getEnumFilterValue(filters?.status, "status", EventStatus),
        ...(options?.templateId
          ? { eventTemplateId: { equals: options.templateId } }
          : {}),
        ...(options.excludeRecurringEvents
          ? { eventTemplateId: { equals: null } }
          : {}),
      },
      orderBy,
    }),
    [
      filters?.name,
      filters?.status,
      filters?.type,
      orderBy,
      page,
      pageSize,
      venue,
      options?.templateId,
      options?.excludeRecurringEvents,
    ]
  );

  const { data, loading } = useEventListQuery({
    variables,
  });

  const onChange = useCallback<UseEventListReturnType["onChange"]>(
    ({ current, pageSize: newPageSize }, newFilters, sorter) => {
      setTableQueryParams({
        page: current,
        pageSize: newPageSize,
        sorter,
        filters: newFilters,
      });
    },
    [setTableQueryParams]
  );

  const { nodes: dataSource, count: total } = data?.events || {};

  return useMemo(
    () => ({
      dataSource,
      loading,
      onChange,
      pagination: {
        pageSize,
        current: page,
        total,
      },
    }),
    [loading, onChange, page, pageSize, total, dataSource]
  );
};

export const useEventColumns = () => {
  const intl = useIntl();
  const [{ sortOrder, filters }] = useTableQueryParams<Event>();

  const columns: ColumnsType<Event> = useMemo(
    () => [
      {
        title: intl.formatMessage({
          defaultMessage: "Name",
        }),
        dataIndex: "name",
        key: "name",
        sorter: {
          multiple: 1,
        },
        sortOrder: sortOrder.name,
        ...getColumnTextFilterProps("name", filters?.name),
      },
      {
        title: intl.formatMessage({
          defaultMessage: "Date",
        }),
        key: "date",
        dataIndex: "date",
        sorter: {
          multiple: 2,
        },
        sortOrder: sortOrder.date,
        render: renderTableDateZonedColumn("date", "timezone"),
      },
      {
        title: intl.formatMessage({
          defaultMessage: "Type",
        }),
        key: "type",
        dataIndex: "type",
        sorter: {
          multiple: 3,
        },
        sortOrder: sortOrder.type,
        ...getColumnEnumFilterProps("type", filters?.type, false, EventType),
      },
      {
        title: intl.formatMessage({
          defaultMessage: "Status",
        }),
        key: "status",
        dataIndex: "status",
        sorter: {
          multiple: 4,
        },
        sortOrder: sortOrder.status,
        ...getColumnEnumFilterProps(
          "status",
          filters?.status,
          false,
          EventStatus
        ),
      },
      {
        title: intl.formatMessage({
          defaultMessage: "Actions",
        }),
        key: "action",
        render: (_, { id }) => (
          <Space size="middle">
            <Link
              to={generatePath(ROUTE_PATHS.EventEdit, {
                id: id.toString(),
              })}
            >
              <EditOutlined />
            </Link>
            <Link
              to={generatePath(ROUTE_PATHS.EventDetails, {
                id: id.toString(),
              })}
            >
              <EyeOutlined />
            </Link>
            <Link to={`${ROUTE_PATHS.Dashboard}?eventId=${id}`}>
              <CalendarOutlined />
            </Link>
          </Space>
        ),
      },
    ],
    [
      intl,
      sortOrder.name,
      sortOrder.date,
      sortOrder.type,
      sortOrder.status,
      filters?.name,
      filters?.type,
      filters?.status,
    ]
  );

  return { columns };
};
